import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { deleteServices, getAllServices } from "../../../api/services/api";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../../components/DrawerTemplate";
import EmptyScreen from "../../../components/EmptyScreen";
import { DeleteButton, EditButton } from "../../../components/IconButtons";
import Loader from "../../../components/Loader";
import { ModernHeader } from "../../../components/modern";
import { RoleGuard } from "../../../components/RBAC";
import Table from "../../../components/TableComponent";
import { ServiceForm } from "./ServiceForm";

export const Services = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);

  const toggleDrawer = (newOpen: any) => {
    setOpen(newOpen);
  };

  const { data, isLoading } = useQuery(
    [
      "services",
      {
        search: search,
        limit: limit,
        offset: limit * page,
      },
    ],
    getAllServices
  );

  // Debug: Log first service data structure
  // console.log(JSON.stringify(data?.data?.services?.slice(0, 1)));

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { mutate: deleteServicesMutate } = useMutation(deleteServices, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries("services");
      toast.success(res?.data?.message);
    },
    onError: (res: any) => {
      toast.error(res?.response?.data?.message);
    },
  });

  const handleEditClick = (data: any) => {
    setFormData(data);
    toggleDrawer(true);
  };
  const handleDeleteClick = (id: any) => {
    deleteServicesMutate(id);
  };

  const handleAdd = () => {
    toggleDrawer(true);
    setFormData(null);
  };

  const Columns = [
    {
      title: "Service Name",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Symptoms",
      dataIndex: "symptom",
      key: "symptom",
      render: (colData: any, row: any) => {
        // Access 'symptom' field (singular) from API response
        const symptoms = row?.symptom || [];

        if (symptoms.length === 0) {
          return (
            <Typography sx={{ fontSize: "14px", color: "#999" }}>
              No symptoms
            </Typography>
          );
        }

        const categories = symptoms.map((i: any, index: number) => (
          <Chip
            key={index}
            label={i?.title || "Unknown"}
            size="small"
            sx={{
              backgroundColor: "#1212121A",
              borderRadius: "15px",
              margin: "2px 4px",
              fontWeight: 500,
            }}
          />
        ));

        const rows = [];
        for (let i = 0; i < categories?.length; i += 7) {
          rows.push(
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              {categories.slice(i, i + 7)}
            </Box>
          );
        }

        return <Box>{rows}</Box>;
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (colData: any, row: any) => (
        <Box display="flex" gap="20px">
          <EditButton onClick={() => handleEditClick(row)} />
          <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
            <DeleteButton onClick={() => handleDeleteClick(colData)} />
          </RoleGuard>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", pb: 4 }}>
      <ModernHeader
        title="Services Management"
        subtitle={`Total: ${data?.data?.count || 0} services`}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Add New Service
          </Button>
        }
      />

      <Box px={3}>
        {/* Search Section */}
        <Box
          sx={{
            backgroundColor: "#fff",
            p: 2.5,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            mb: 3,
          }}
        >
          <TextField
            label="Search by Service Name"
            placeholder="Enter service name..."
            sx={{
              minWidth: "300px",
              flex: 1,
              maxWidth: "500px",
            }}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              ),
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        {/* Table Section */}
        {data?.data?.count === 0 ? (
          <EmptyScreen type="services" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <Table
                  headAlign={"center"}
                  tableAlign={"center"}
                  key={"services"}
                  dataSource={data?.data?.services}
                  rowKey={"id"}
                  columns={Columns}
                  loading={isLoading}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <TablePagination
                    count={data?.data?.count || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Drawer for Add/Edit Service */}
      <Drawer anchor="right" open={open} onClose={() => toggleDrawer(false)}>
        <StyledDrawerHeader>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formData ? "Edit Service" : "Add New Service"}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
              "&:hover": { opacity: 0.8 },
            }}
            onClick={() => toggleDrawer(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          <ServiceForm
            formData={formData}
            toggleDrawer={(e: any) => toggleDrawer(e)}
          />
        </StyledDrawerContent>
      </Drawer>
    </Box>
  );
};
